import { DismissIcon, Flex, SearchField, SearchIcon } from '@iheartjane/reefer';
import { useCallback, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useSearchParams } from 'react-router';

import { useRecommendedSort } from '@jane/dm/internal';
import { buildSearchFilter } from '@jane/search/util';
import { useGetStore } from '@jane/shared/data-access';
import type { Store } from '@jane/shared/types';

import {
  CartButton,
  FilterAndSort,
  Header,
  IconButton,
  MenuProducts,
  NoResults,
  TitleSearchField,
  getColumnCount,
} from '../../components';
import type { RootLoaderType } from '../../data-access';
import { searchOptions, useGetCart } from '../../data-access';
import { useKioskSpacing, useTypedRouteLoaderData } from '../../hooks';
import { useProductSort, useSearchContext } from '../../providers';
import { trackSearchedMenuProducts } from '../../util';

export const SearchResults = () => {
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const searchRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const { resetSearchState, searchState } = useSearchContext();
  const query = searchState.searchText ?? queryParams.get('searchText') ?? '';
  const { getKioskSpacing, isIpad } = useKioskSpacing();
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });

  const { data: cart } = useGetCart();

  const defaultSortName = store.optional_filters ? 'Featured' : 'Relevance';
  const { optionalFiltersString, searchSort } = useProductSort({
    defaultSortName,
    hasThcProducts: true,
    optionalFilters: store.optional_filters,
    store,
  });

  const options = searchOptions({
    optionalFilters: optionalFiltersString,
    storeId,
  });

  const searchFilter = buildSearchFilter({
    searchState,
    staticFilters: options.staticFilters,
  });

  const productsQuery = useRecommendedSort({
    numColumns: getColumnCount(),
    searchAttributes: ['*'],
    searchFilter,
    searchQuery: query,
    searchSort,
    storeId,
  });

  useEffect(() => {
    trackSearchedMenuProducts();
  }, [productsQuery.products]);

  const updateSearchTerm = useCallback(
    (nextTerm: string) => {
      resetSearchState({ searchText: nextTerm });
    },
    [resetSearchState]
  );

  const navigateToMenu = useCallback(() => {
    navigate(`/stores/${storeId}/menu`, {
      state: { resetSearch: true },
    });
  }, [navigate, storeId]);

  const headerPadding = {
    pb: getKioskSpacing(40, 32),
    pt: getKioskSpacing(96, 56),
    px: getKioskSpacing(64, 40),
  };

  const isResultsEmpty = productsQuery.isSuccess && productsQuery.numHits === 0;

  return (
    <>
      <Header
        alignItems="center"
        justifyContent="space-between"
        mb={0}
        sticky
        {...headerPadding}
      >
        <IconButton
          ariaLabel="navigate back"
          icon={
            <DismissIcon altText="dismiss icon" size={isIpad ? 'lg' : 'xl'} />
          }
          onClick={navigateToMenu}
          spacing={{
            marginRight: '24px',
          }}
        />

        <TitleSearchField>
          <SearchIcon color="grays-black" size={isIpad ? 'lg' : 'xl'} />
          <SearchField
            autocomplete="off"
            debounceDelay={300}
            defaultValue={query}
            enterKeyHint="search"
            hideSearchIcon
            isDebounced
            label="Search products"
            labelHidden
            name="product-search"
            onChange={updateSearchTerm}
            onEnterPress={() => {
              searchRef.current?.blur();
            }}
            placeholder="Search all products"
            ref={searchRef}
            width="100%"
          />
        </TitleSearchField>

        <CartButton
          cart={cart}
          onClick={() => navigate(`/stores/${storeId}/cart`)}
          storeId={storeId}
        />
      </Header>

      <FilterAndSort
        defaultSortName={defaultSortName}
        mb={getKioskSpacing(48, 32)}
        mt={getKioskSpacing(32, 24)}
        optionalFilters={store.optional_filters}
        showAlphabeticalSort={false}
        showTHCSort={Boolean(productsQuery.searchResultFacets?.['percent_thc'])}
        searchProps={productsQuery}
      />
      {!!query &&
        (isResultsEmpty ? (
          <Flex
            flexDirection="column"
            grow={1}
            position="relative"
            px={getKioskSpacing(64, 48)}
            height="100%"
            width="100%"
          >
            <NoResults />
          </Flex>
        ) : (
          <MenuProducts category="all" searchProps={productsQuery} />
        ))}
      <Outlet />
    </>
  );
};
